/**
 * `BusinessAccountDto` / `BusinessAccountBindingModel`, from
 * `Gopanza.Api.Services.Abstractions`.
 *
 * This is the payout account behind a store: bank details, the fee split, and
 * the identity evidence a payment processor asks for. `businesses/accounts`
 * returns all of it in the clear — see `docs/api-findings-admin-services.md`.
 * Nothing here is masked on the way in, so the masking is the page's job.
 */

/** `StripeEntityType`. */
export const ENTITY_TYPE = { individual: 0, company: 1 } as const;

/**
 * `BusinessType` — the processor's merchant category. Nineteen values whose
 * names are the label, so they are rendered from the enum rather than
 * translated: they are a processor's taxonomy, not our words.
 */
export const BUSINESS_TYPES = [
  'AntiqueShops',
  'ArtistsSupplyCraftShops',
  'AutomotivePartsAccessorieStores',
  'BicycleShops',
  'BookStores',
  'CameraPhotographicSupplyStores',
  'CandyNutConfectioneryStores',
  'ChildrensInfantsWearStores',
  'CosmeticStores',
  'DepartmentStores',
  'ElectronicsStores',
  'GroceryStoresSupermarkets',
  'HardwareStores',
  'JewelryStores',
  'MensWomensClothingStores',
  'MiscellaneousFoodStoresConvenienceStoresSpecialtyMarkets',
  'MusicStores',
  'EatingPlacesRestaurant',
] as const;

/** `AntiqueShops` → `Antique Shops`. */
export function businessTypeLabel(value: number): string {
  const name = BUSINESS_TYPES[value];
  if (!name) return '—';
  return name.replace(/([a-z])([A-Z])/g, '$1 $2');
}

export interface BusinessAccount {
  id: string;
  businessId: string | null;
  businessName: string | null;
  accountId: string | null;
  userId: string | null;
  isActive: boolean;
  allowPayouts: boolean;
  minimumPayoutAmount: number;

  accountName: string | null;
  accountHolderName: string | null;
  accountHolderType: number;
  /** Full, in the clear. Never rendered whole without a deliberate reveal. */
  accountNumber: string | null;
  routingNumber: string | null;

  firstName: string | null;
  lastName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  ein: string | null;
  last4Ssn: string | null;
  birthDate: string | null;

  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;

  subtotalPercentage: number;
  taxesPercentage: number;
  serviceFeePercentage: number;
  processorPercentage: number;
  payPalClientId: string | null;
  businessType: number;

  createdAt: string;
  acceptanceDate: string;
  acceptanceIp: string | null;
  creatorCompleteName: string | null;
  creatorAvatar: string | null;
}

/** Exactly what create and update accept. */
export interface BusinessAccountInput {
  businessId: string | null;
  minimumPayoutAmount: number;
  accountHolderType: number;
  contactPhone: string;
  contactEmail: string;
  accountId: string;
  userId: string | null;
  firstName: string;
  lastName: string;
  businessName: string;
  accountName: string;
  EIN: string;
  acceptanceIp: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  DOB: string;
  birthDate: string | null;
  last4Ssn: string;
  country: string;
  accountHolderName: string;
  accountNumber: string;
  routingNumber: string;
  businessType: number;
  isActive: boolean;
  subtotalPercentage: number;
  taxesPercentage: number;
  serviceFeePercentage: number;
  processorPercentage: number;
  payPalClientId: string;
  allowPayouts: boolean;
}

/**
 * The last four digits, and nothing else.
 *
 * Used everywhere a bank number is shown without an explicit reveal — the
 * original printed both numbers in full as table columns, so an account number
 * was visible to anyone glancing at the screen and captured by any screenshot
 * of the list.
 */
export function maskTail(value: string | null, keep = 4): string {
  if (!value) return '—';
  const trimmed = value.trim();
  if (trimmed.length <= keep) return '•'.repeat(trimmed.length);
  return `•••• ${trimmed.slice(-keep)}`;
}

export function toInput(account: BusinessAccount): BusinessAccountInput {
  return {
    businessId: account.businessId,
    minimumPayoutAmount: account.minimumPayoutAmount,
    accountHolderType: account.accountHolderType,
    contactPhone: account.contactPhone ?? '',
    contactEmail: account.contactEmail ?? '',
    accountId: account.accountId ?? '',
    userId: account.userId,
    firstName: account.firstName ?? '',
    lastName: account.lastName ?? '',
    businessName: account.businessName ?? '',
    accountName: account.accountName ?? '',
    EIN: account.ein ?? '',
    acceptanceIp: account.acceptanceIp ?? '',
    address1: account.address1 ?? '',
    address2: account.address2 ?? '',
    city: account.city ?? '',
    state: account.state ?? '',
    postalCode: account.postalCode ?? '',
    DOB: '',
    birthDate: account.birthDate,
    last4Ssn: account.last4Ssn ?? '',
    country: account.country ?? 'US',
    accountHolderName: account.accountHolderName ?? '',
    accountNumber: account.accountNumber ?? '',
    routingNumber: account.routingNumber ?? '',
    businessType: account.businessType,
    isActive: account.isActive,
    subtotalPercentage: account.subtotalPercentage,
    taxesPercentage: account.taxesPercentage,
    serviceFeePercentage: account.serviceFeePercentage,
    processorPercentage: account.processorPercentage,
    payPalClientId: account.payPalClientId ?? '',
    allowPayouts: account.allowPayouts,
  };
}

export function emptyAccount(): BusinessAccountInput {
  return {
    businessId: null,
    minimumPayoutAmount: 0,
    accountHolderType: ENTITY_TYPE.company,
    contactPhone: '',
    contactEmail: '',
    accountId: '',
    userId: null,
    firstName: '',
    lastName: '',
    businessName: '',
    accountName: '',
    EIN: '',
    acceptanceIp: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    DOB: '',
    birthDate: null,
    last4Ssn: '',
    country: 'US',
    accountHolderName: '',
    accountNumber: '',
    routingNumber: '',
    businessType: 11,
    isActive: false,
    subtotalPercentage: 0,
    taxesPercentage: 0,
    serviceFeePercentage: 0,
    processorPercentage: 0,
    payPalClientId: '',
    allowPayouts: false,
  };
}
